import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import { buildTaxaIndex } from '../../Sinks/Selector/TaxaIndexCore.ts'
import type { KronaFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import type { TaxonEntry } from '../../Sources/TaxaNavigatorPanel.tsx'

const selectorCache = createSelectorCache<TransformResult<TaxonEntry[]>>()

export const TAXA_SUGGESTION_LIMIT = 20

export function selectTaxaNavigatorIndex(
  state: typeof State,
  sourceId: SourceId,
  _bucketKey: string | undefined,
  opts: KronaFilterOpts,
  optsKey: string
): TransformResult<TaxonEntry[]> {
  const cacheId = `${sourceId}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [(s: typeof State) => selectCubeContext(s, sourceId)],
      (ctx): TransformResult<TaxonEntry[]> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const grade = opts.gradeThreshold ?? 0
        const sampleIds = new Set<string>()
        for (const bucket of ctx.selectedBuckets) {
          for (const s of bucket.samples) sampleIds.add(s.id)
        }

        // Index-building lives in the pure TaxaIndexCore so the workspace and the server-side chart-render
        // sidecar (chat `taxonEntry` zoom) share ONE implementation. A cube without a taxonomy ladder yields
        // [] — callers treat empty as "nothing to resolve" and stay at root.
        return { status: 'ready', value: buildTaxaIndex(ctx.table, sampleIds, grade) }
      }
    )

  )(state)
}

/**
 * Ranks the "Jump to taxon" suggestions before capping: capping an alphabetically ordered
 * substring filter drops the wanted taxon off the end.
 */
export function rankTaxonSuggestions(
  index: readonly TaxonEntry[],
  query: string,
  limit: number = TAXA_SUGGESTION_LIMIT
): { suggestions: TaxonEntry[]; totalMatches: number } {
  const q = query.trim().toLowerCase()
  if (!q) return { suggestions: [], totalMatches: 0 }

  const matches: { entry: TaxonEntry; tier: number }[] = []
  for (const entry of index) {
    const label = entry.label.toLowerCase()
    const at = label.indexOf(q)
    if (at < 0) continue
    matches.push({ entry, tier: label === q ? 0 : at === 0 ? 1 : 2 })
  }

  matches.sort((a, b) => a.tier - b.tier || a.entry.level - b.entry.level || a.entry.label.localeCompare(b.entry.label))

  return { suggestions: matches.slice(0, limit).map((m) => m.entry), totalMatches: matches.length }
}
